import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  CreateEventRegistration,
  Event,
  EventRegistrationBillingAddress,
  EventRegistrationBillingMethod,
  EventRegistrationForm,
  EventRegistrationFormStatus,
  EventRegistrationResponse,
  EventRegistrationSelection,
  Problem,
} from '../generated/api/dsv-public/model';
import { getEvent, getEventRegistrationForm, saveEventRegistration } from '../generated/api/dsv-public/dsv-public';
import { RegistrantInfo } from '../forms/form.types';

type EventRegistrationFormContextProps = {
  eventId: number;
  isLoading: boolean;
  isError: boolean;
  isLoadingSubmission: boolean;
  isSubmissionError: boolean;
  isApiError: boolean;
  isInactive: boolean;
  isExistingForm: boolean;
  isExistingEvent: boolean;
  registrationForm: EventRegistrationForm | undefined;
  event: Event | undefined;
  registrationFormIsActive: boolean | undefined;
  apiResponseProblem: Problem | undefined;
  errorResponseCode: number | undefined;
  eventRegistrationResponse: EventRegistrationResponse | undefined;
  optionsPriceMap: Map<number, number>;
  optionsCurrencyMap: Map<number, string>;
  submitForm: (
    registrant: RegistrantInfo,
    registrationSelection: EventRegistrationSelection,
    registrationEntries: CreateEventRegistration | CreateEventRegistration[],
    billingAddress?: EventRegistrationBillingAddress,
    billingMethod?: EventRegistrationBillingMethod
  ) => void;
  resetFormSubmission: () => void;
};

export const EventRegistrationFormContext = createContext<EventRegistrationFormContextProps>(
  {} as EventRegistrationFormContextProps
);

type EventRegistrationFormProviderProps = {
  children: React.ReactNode;
  eventId: number;
};

export const EventRegistrationFormProvider: React.FC<EventRegistrationFormProviderProps> = ({ children, eventId }) => {
  const [registrationForm, setRegistrationFrom] = useState<EventRegistrationForm>();
  const [event, setEvent] = useState<Event>();
  const [apiResponseProblem, setApiResponseProblem] = useState<Problem>();
  const [errorResponseCode, setErrorResponseCode] = useState<number>();
  const [eventRegistrationResponse, setEventRegistrationResponse] = useState<EventRegistrationResponse>();
  const [registrationFormIsActive, setRegistrationFormIsActive] = useState<boolean>();
  const [isError, setIsError] = useState<boolean>(false);
  const [isLoadingSubmission, setIsLoadingSubmission] = useState<boolean>(false);
  const [isSubmissionError, setIsSubmissionError] = useState<boolean>(false);
  const [isExistingForm, setIsExistingForm] = useState<boolean>(true);
  const [isExistingEvent, setIsExistingEvent] = useState<boolean>(true);
  const isFirstCall = useRef<boolean>(true);

  const setErrorResponse = useCallback((error: any): Problem => {
    setIsError(true);
    setErrorResponseCode(error.response?.status);
    if (error.response.headers['content-type'].includes('application/problem+json')) {
      setApiResponseProblem(error.response.data as Problem);
    }
    return error.response.data as Problem;
  }, []);

  const fetchEventRegistrationForm = useCallback(() => {
    getEventRegistrationForm(eventId)
      .then(data => {
        setRegistrationFrom(data as EventRegistrationForm);
      })
      .catch(error => {
        const problem = setErrorResponse(error);
        if (problem?.problemCode === 'EVENT_REGISTRATION_FORM_NOT_FOUND') {
          setIsExistingForm(false);
        }
      });
  }, [eventId, setErrorResponse]);

  const fetchEvent = useCallback(() => {
    getEvent(eventId)
      .then(data => {
        setEvent(data as Event);
      })
      .catch(error => {
        setErrorResponse(error);
        if (error.response?.status === 404) {
          setIsExistingEvent(false);
        }
      });
  }, [eventId, setErrorResponse]);

  const submitEventRegistrationForm = useCallback(
    (
      registrant: RegistrantInfo,
      registrationSelection: EventRegistrationSelection,
      registrationEntries: CreateEventRegistration | CreateEventRegistration[],
      billingAddress?: EventRegistrationBillingAddress,
      billingMethod?: EventRegistrationBillingMethod
    ) => {
      setIsLoadingSubmission(true);
      void saveEventRegistration(eventId, {
        registrant,
        registrationSelection,
        registrationEntries: Array.isArray(registrationEntries) ? registrationEntries : [registrationEntries],
        billingAddress: billingAddress,
        billingMethod: billingMethod,
      })
        .then(data => {
          setIsLoadingSubmission(false);
          setEventRegistrationResponse(data as EventRegistrationResponse);
          window.location.hash = '#submission-successful';
        })
        .catch(error => {
          setIsSubmissionError(true);
          setIsLoadingSubmission(false);
          setApiResponseProblem(error.response.data as Problem);
          setErrorResponseCode(error.response.status);
          window.location.hash = '#submission-error';
        });
    },
    [eventId]
  );

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') {
      fetchEventRegistrationForm();
      fetchEvent();
    } else if (isFirstCall.current) {
      fetchEventRegistrationForm();
      fetchEvent();
      isFirstCall.current = false;
    }
  }, [fetchEventRegistrationForm, fetchEvent, isFirstCall]);

  const isValidStatus = useMemo(() => {
    return (
      registrationForm?.status === EventRegistrationFormStatus.OPEN ||
      registrationForm?.status === EventRegistrationFormStatus.WAITING_LIST
    );
  }, [registrationForm]);

  const optionsPriceMap = useMemo(() => {
    const map = new Map<number, number>();
    registrationForm?.pricingOptionGroups?.forEach(group => {
      group.options?.forEach(option => {
        if (option.optionId && option.price) {
          map.set(option.optionId, option.price);
        }
      });
    });
    return map;
  }, [registrationForm]);

  const optionsCurrencyMap = useMemo(() => {
    const map = new Map<number, string>();
    registrationForm?.pricingOptionGroups?.forEach(group => {
      group.options?.forEach(option => {
        if (option.optionId && option.currency) {
          map.set(option.optionId, option.currency);
        }
      });
    });
    return map;
  }, [registrationForm]);

  useEffect(() => {
    setRegistrationFormIsActive(isValidStatus);
  }, [isValidStatus]);

  const isLoading = useMemo(() => {
    return registrationForm === undefined || event === undefined;
  }, [registrationForm, event]);

  const isApiError = useMemo(() => {
    return apiResponseProblem !== undefined;
  }, [apiResponseProblem]);

  const isInactive = useMemo(() => {
    return !isLoading && !isError && !registrationFormIsActive;
  }, [isLoading, isError, registrationFormIsActive]);

  const handleResetFormSubmission = useCallback(() => {
    setIsSubmissionError(false);
  }, []);

  return (
    <EventRegistrationFormContext.Provider
      value={{
        eventId: eventId,
        isLoading: isLoading,
        isError: isError,
        isLoadingSubmission: isLoadingSubmission,
        isSubmissionError: isSubmissionError,
        isApiError: isApiError,
        isInactive: isInactive,
        isExistingForm: isExistingForm,
        isExistingEvent: isExistingEvent,
        registrationForm: registrationForm,
        event: event,
        registrationFormIsActive: registrationFormIsActive,
        apiResponseProblem: apiResponseProblem,
        errorResponseCode: errorResponseCode,
        eventRegistrationResponse: eventRegistrationResponse,
        submitForm: submitEventRegistrationForm,
        resetFormSubmission: handleResetFormSubmission,
        optionsPriceMap: optionsPriceMap,
        optionsCurrencyMap: optionsCurrencyMap,
      }}
    >
      {children}
    </EventRegistrationFormContext.Provider>
  );
};

export const useEventRegistrationForm = (): EventRegistrationFormContextProps =>
  useContext(EventRegistrationFormContext);
